import { Anchor, Group, Skeleton, Stack, Text, Timeline, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyState, TextSerif } from "@/components/elements";
import { AuctionCarHistory } from "@/types/auctions";

import type { FC } from "react";

export type CarHistoryProps = {
  history?: AuctionCarHistory[];
  loading?: boolean;
};

type RenderItemKey = keyof AuctionCarHistory;

export const CarHistory: FC<CarHistoryProps> = ({ history = [], loading = false }) => {
  const { t } = useTranslation();

  function renderItem(key: RenderItemKey, item: AuctionCarHistory) {
    if (item[key]) {
      const value = item[key];

      switch (key) {
        case "date":
        case "name":
        case "event_website":
          return null;
        case "description":
          return <Text key={key}>{value}</Text>;
        case "event":
          if (item.event_website) {
            return (
              <Group key={key} align="baseline" gap="sm">
                <TextSerif>{t(`components.CarHistory.labels.${key}`, key)}:</TextSerif>
                <Anchor
                  c="inherit"
                  href={item.event_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="not-hover"
                >
                  {value}
                </Anchor>
              </Group>
            );
          }
        default:
          return (
            <Group key={key} align="baseline" gap="sm">
              <TextSerif>{t(`components.CarHistory.labels.${key}`, key)}:</TextSerif>
              <Text>{value}</Text>
            </Group>
          );
      }
    }

    return null;
  }

  return (
    <Stack>
      <Title order={3}>{t("components.CarHistory.title")}</Title>
      <Skeleton visible={loading}>
        {history.length > 0 ? (
          <Timeline lineWidth={3}>
            {history.map((item, index) => (
              <Timeline.Item
                key={index}
                title={
                  <TextSerif mb="sm" c="gray" title={item.date}>
                    {item.name}
                  </TextSerif>
                }
              >
                <Stack gap="xs">
                  {(Object.keys(item) as RenderItemKey[]).map((key) => renderItem(key, item))}
                </Stack>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <EmptyState type="noData" text={t("common.noData")} h={300} />
        )}
      </Skeleton>
    </Stack>
  );
};
